import { useEffect, useState, useRef, useLayoutEffect, useContext } from "react";
import { subscribeToSystemMessages } from "../api/api";
import { SystemMessage as SystemMessageType } from "../types";
import "./SystemMessage.scss";
import { AppContext } from "../State/Context";
import { ActionType } from "../State/State";
import { getWorldCoordsToCenterHex } from "../State/stateUtils";

interface MessageWithId extends SystemMessageType {
  id: number;
  isLeaving?: boolean;
}

export const SystemMessage = () => {
  const [messages, setMessages] = useState<MessageWithId[]>([]);
  const [, forceUpdate] = useState({});
  const nextIdRef = useRef(0);
  const messageRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
  const { state, dispatch } = useContext(AppContext);

  useEffect(() => {
    subscribeToSystemMessages((message) => {
      const messageId = nextIdRef.current++;
      const newMessage: MessageWithId = {
        ...message,
        id: messageId,
      };

      setMessages((prev) => [...prev, newMessage]);

      // Start fade out animation after 4.5 seconds
      setTimeout(() => {
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === newMessage.id ? { ...msg, isLeaving: true } : msg
          )
        );
      }, 4500);

      // Remove message after 5 seconds
      setTimeout(() => {
        setMessages((prev) => prev.filter((msg) => msg.id !== newMessage.id));
        // Clean up ref
        delete messageRefs.current[newMessage.id];
      }, 5000);
    });

    return () => {
      setMessages([]);
      messageRefs.current = {};
    };
  }, []); // Empty dependency array - subscribe only once

  // Force re-render after DOM updates to recalculate positions
  useLayoutEffect(() => {
    if (messages.length > 0) {
      forceUpdate({});
    }
  }, [messages]);

  // Calculate bottom position for each message
  const getBottomPosition = (index: number) => {
    let bottom = 40; // Initial offset from bottom

    for (let i = 0; i < index; i++) {
      const messageId = messages[i]?.id;
      if (messageId !== undefined && messageRefs.current[messageId]) {
        const height = messageRefs.current[messageId]?.offsetHeight || 0;
        bottom += height + 10; // Add height + 10px gap
      }
    }

    return bottom;
  };

  return (
    <>
      {messages.map((message, index) => {
        const classes =
          message.systemMessage === "error"
            ? "border-red-600 text-red-600"
            : "border-green-600 text-green-600";

        const onClick = () => {
          if (message.navigateTo) {
            const { x, y } = message.navigateTo;
            dispatch({ type: ActionType.SELECT_HEX, x, y });
            dispatch({ type: ActionType.OPEN_HEX_INFO_POPUP });
            const coords = getWorldCoordsToCenterHex(x, y, state.worldScale);
            dispatch({ type: ActionType.CHANGE_WORLD_COORDINATES, x: coords.worldX, y: coords.worldY });
          }
        };

        return (
          <div
            key={message.id}
            ref={(el) => {
              messageRefs.current[message.id] = el;
            }}
            onClick={onClick}
            className={
              classes +
              " system-message fixed border-2 right-2 bg-white p-3 z-[1000000000000] transition-all duration-500 shadow-lg flex gap-3 items-center" +
              (message.isLeaving
                ? " opacity-0 translate-x-full"
                : " opacity-95 translate-x-0") +
              (message.navigateTo ? " cursor-pointer" : "")
            }
            style={{
              bottom: `${getBottomPosition(index)}px`,
              maxWidth: "360px",
              width: "max-content",
              wordWrap: "break-word",
              overflowWrap: "break-word",
            }}
          >
            {message.imageUrl && (
              <img
                src={message.imageUrl}
                alt="hex"
                width={56}
                height={56}
                className="rounded"
              />)
            }
            <div className="flex flex-col">
              {message.title && (
                <div className="font-semibold text-sm mb-1">{message.title}</div>
              )}
              <div className="text-[13px] leading-snug whitespace-pre-line">
                {message.navigateToPlayerUid && message.playerName
                  ? (<>
                      Player <span className="underline cursor-pointer" onClick={(e) => { e.stopPropagation(); dispatch({ type: ActionType.SELECT_PLAYER, uid: message.navigateToPlayerUid! }); dispatch({ type: ActionType.TOGGLE_PLAYER_INFO_POPUP }); }}>{message.playerName}</span> joined the game
                    </>)
                  : message.text}
              </div>
            </div>
          </div>
        );
      })}
    </>
  );
};
